import React, { useEffect, useState } from 'react';
import './RevenueAnalytics.css';
import { Box, Typography, Grid } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import RevenueLineChart from './RevenueLineChart/RevenueLineChart';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { CardSkeleton, ChartSkeleton } from '../../SkeletonLoader/SkeletonLoader';

const RevenueAnalytics = ({ timePeriod = 'last_month' }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [lifetimeData, setLifetimeData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Map component timePeriod to API period values
  const getApiPeriod = timePeriod => {
    switch (timePeriod) {
      case 'last_day':
        return 'today';
      case 'last_week':
        return '1_week';
      case 'last_2_weeks':
        return '2_weeks';
      case 'last_month':
        return '1_month';
      case 'last_3_months':
        return '3_months';
      case 'last_6_months':
        return '6_months';
      case 'last_year':
        return '1_year';
      default:
        return '1_month';
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchAnalytics = async () => {
      try {
        setLoading(true);

        const apiPeriod = getApiPeriod(timePeriod);

        // Fetch revenue metrics (time-based)
        const revenueResponse = await axiosInstance.get(
          `/admin/dashboard/revenue-metrics?period=${apiPeriod}`
        );

        // Fetch lifetime value metrics (static, no time period)
        const lifetimeResponse = await axiosInstance.get(`/admin/dashboard/lifetime-value-metrics`);

        // Fetch chart data (keeping the original API)
        const chartResponse = await axiosInstance.get(
          `/admin/dashboard/revenue-analytics?period=${timePeriod}`
        );

        if (isMounted) {
          setAnalyticsData({
            ...revenueResponse.data,
            // Add chart data from the original API
            daily_trend: chartResponse.data?.daily_trend || [],
            weekly_trend: chartResponse.data?.weekly_trend || [],
            monthly_trend: chartResponse.data?.monthly_trend || [],
          });
          setLifetimeData(lifetimeResponse.data);
        }
      } catch (error) {
        if (isMounted) {
          setAnalyticsData(null);
          setLifetimeData(null);
        }
        console.error('Error fetching revenue analytics:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchAnalytics();
    return () => {
      isMounted = false;
    };
  }, [timePeriod]);

  const formatCurrency = amount => {
    if (amount === undefined || amount === null || amount === '--') return '--';
    const num = parseFloat(amount);
    if (isNaN(num)) return '--';
    return `${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Riyal`;
  };

  // Get time period specific data
  const getTimeSpecificData = () => {
    switch (timePeriod) {
      case 'last_day':
        return {
          title: 'Daily Revenue',
          profitTitle: 'Daily Profit',
          avgTitle: 'Average Daily Revenue/User',
          comparisonPeriod: 'previous day',
        };
      case 'last_week':
        return {
          title: 'Weekly Revenue',
          profitTitle: 'Weekly Profit',
          avgTitle: 'Average Weekly Revenue/User',
          comparisonPeriod: 'previous week',
        };
      case 'last_2_weeks':
        return {
          title: '2-Week Revenue',
          profitTitle: '2-Week Profit',
          avgTitle: 'Average 2-Week Revenue/User',
          comparisonPeriod: 'previous 2 weeks',
        };
      case 'last_month':
        return {
          title: 'Monthly Revenue',
          profitTitle: 'Monthly Profit',
          avgTitle: 'Average Monthly Revenue/User',
          comparisonPeriod: 'last month',
        };
      case 'last_3_months':
        return {
          title: '3-Month Revenue',
          profitTitle: '3-Month Profit',
          avgTitle: 'Average 3-Month Revenue/User',
          comparisonPeriod: 'last 3 months',
        };
      case 'last_6_months':
        return {
          title: '6-Month Revenue',
          profitTitle: '6-Month Profit',
          avgTitle: 'Average 6-Month Revenue/User',
          comparisonPeriod: 'last 6 months',
        };
      case 'last_year':
        return {
          title: 'Yearly Revenue',
          profitTitle: 'Yearly Profit',
          avgTitle: 'Average Yearly Revenue/User',
          comparisonPeriod: 'previous year',
        };
      default:
        return {
          title: 'Monthly Revenue',
          profitTitle: 'Monthly Profit',
          avgTitle: 'Average Monthly Revenue/User',
          comparisonPeriod: 'last month',
        };
    }
  };

  const timeData = getTimeSpecificData();

  // Card data mapping using new API structure
  const cardData = [
    {
      title: timeData.title,
      value: formatCurrency(analyticsData?.total_revenue?.value),
      change: analyticsData?.total_revenue?.growth_percentage,
      period: timeData.comparisonPeriod,
    },
    {
      title: timeData.profitTitle,
      value: formatCurrency(analyticsData?.total_profit?.value),
      change: analyticsData?.total_profit?.growth_percentage,
      period: timeData.comparisonPeriod,
    },
    {
      title: timeData.avgTitle,
      value: formatCurrency(analyticsData?.avg_revenue_per_user?.value),
      change: analyticsData?.avg_revenue_per_user?.growth_percentage,
      period: timeData.comparisonPeriod,
    },
    {
      title: 'Average Profit/User',
      value: formatCurrency(analyticsData?.avg_profit_per_user?.value),
      change: analyticsData?.avg_profit_per_user?.growth_percentage,
      period: timeData.comparisonPeriod,
    },
    {
      title: 'Instructor Lifetime Value',
      value: formatCurrency(lifetimeData?.instructor_lifetime_value),
      change: null, // No growth rate for lifetime values
      period: null,
    },
    {
      title: 'Learner Lifetime Value',
      value: formatCurrency(lifetimeData?.learner_lifetime_value),
      change: null, // No growth rate for lifetime values
      period: null,
    },
  ];

  // Prepare chart data from API
  const revenueLineChartData = {
    dailyTrend: analyticsData?.daily_trend || [],
    weeklyTrend: analyticsData?.weekly_trend || [],
    monthlyTrend: analyticsData?.monthly_trend || [],
  };

  // Card UI
  const AnalyticsCard = ({ title, value, change, period }) => (
    <div className="revenueanalytics-card">
      <div className="revenueanalytics-card-title">{title}</div>
      <div className="revenueanalytics-card-value">{value}</div>
      {change !== null && period !== null && (
        <div
          className={`revenueanalytics-card-change ${parseFloat(change) >= 0 ? 'revenueanalytics-positive-change' : 'revenueanalytics-negative-change'}`}
          style={{ opacity: change === '--' ? 0.5 : 1 }}
        >
          {change === '--' ? (
            '--'
          ) : parseFloat(change) >= 0 ? (
            <TrendingUp fontSize="small" />
          ) : (
            <TrendingDown fontSize="small" />
          )}
          {change === '--' ? '--' : `${Math.abs(change).toFixed(1)}%`} vs {period}
        </div>
      )}
    </div>
  );

  return (
    <Box className="revenueanalytics-section">
      <Typography className="revenueanalytics-section-title-revenue">Revenue Analytics</Typography>
      <Grid container spacing={2} className="revenueanalytics-cards">
        {cardData.map((card, idx) => (
          <Grid item xs={12} sm={6} md={4} key={card.title}>
            {loading ? <CardSkeleton /> : <AnalyticsCard {...card} />}
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2} className="revenueanalytics-charts">
        <Grid item xs={12}>
          {loading ? <ChartSkeleton /> : <RevenueLineChart data={revenueLineChartData} />}
        </Grid>
      </Grid>
    </Box>
  );
};

export default RevenueAnalytics;
